import { connect } from 'react-redux';
import { Helmet } from 'react-helmet'; // <-- 1. Import Helmet
import { useEffect, useState, Fragment } from 'react';
import { createHashHistory } from 'history';
import { Link } from 'react-router-dom'; // ✅ Added Link import
// Import all required actions and constants
import { getAllDoctors } from 'src/actions/doctorActions';
import { getAllPatientProfiles } from 'src/actions/patientProfileActions';
import { getDoctorProfileById } from 'src/actions/doctorActions';
import { 
    getReviewsByDoctorId, 
    createReviewFeedback,
    updateReviewFeedback, 
    deleteReviewFeedback
} from 'src/actions/reviewActions';
import { imgUrl } from 'src/actions/types';
import MessageModal from '../Pages/MessageModel';

const history = createHashHistory();

// --- Helper function to render average rating stars ---
const renderStars = (rating) => {
    const stars = [];
    const numRating = Math.round(rating) || 0; 
    for (let i = 0; i < 5; i++) {
        stars.push(<i key={i} className={`fas fa-star${i < numRating ? ' filled' : ''}`} />);
    }
    return stars;
};

// --- Helper function for "time ago" format ---
const formatTimeAgo = (dateString) => {
  if (!dateString) {
    return '';
  }
  try {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.round((now - date) / 1000);
    const intervals = [
      { label: 'year', seconds: 31536000 },
      { label: 'month', seconds: 2592000 },
      { label: 'day', seconds: 86400 },
      { label: 'hour', seconds: 3600 },
      { label: 'minute', seconds: 60 }
    ];
    if (seconds < 30) return 'just now';
    for (const interval of intervals) {
      const count = Math.floor(seconds / interval.seconds);
      if (count >= 1) {
        return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`;
      }
    }
    return `${seconds} second${seconds > 1 ? 's' : ''} ago`;
  } catch (error) {
    console.error("Invalid date for formatting:", dateString);
    return dateString;
  }
};


const Reviews = (props) => {
  // State for the new review form
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  
  // State for editing
  const [editingItem, setEditingItem] = useState({ id: null, text: ''});

  // State for the modal
  const [modalShow, setModalShow] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [modalVariant, setModalVariant] = useState('info');
  const [itemToDelete, setItemToDelete] = useState(null);

  // Get the logged-in doctor's ID
  const patientDataString = localStorage.getItem('loginData');
  const loginData = patientDataString ? JSON.parse(patientDataString) : null;
  const hospitalID = loginData?.hospitalID;

  // Destructure all props for easy access
  const {
    doctors,
    getAllDoctors,
    profiles,
    getAllPatientProfiles,
    reviews,
    getReviewsByDoctorId,
    createReviewFeedback,
    reviewLoading,
    reviewSubmitting,
    updateReviewFeedback,
    deleteReviewFeedback,
  } = props;

  // Effect for fetching data
  useEffect(() => {
    if (hospitalID) {
      getReviewsByDoctorId(hospitalID);
    }
    getAllDoctors();
    getAllPatientProfiles();
  }, [hospitalID,getReviewsByDoctorId,getAllDoctors,getAllPatientProfiles]);

  // --- Utility and Handler Functions ---

  const handleReplySubmit = (e, originalReview) => {
    e.preventDefault();
    if (!replyText.trim()) {
      setModalTitle('Validation Error'); setModalMessage('Please enter a reply.'); setModalVariant('warning'); setModalShow(true); return;
    }
    if (!loginData) {
      setModalTitle('Authentication Required'); setModalMessage('You must be logged in to reply.'); setModalVariant('error'); setModalShow(true); return;
    }
    const feedbackData = {
      PatientID: loginData.hospitalID,
      hospitalID: hospitalID,
      feedback: replyText.trim(),
      reviewId: originalReview.id,
    };
    createReviewFeedback(feedbackData).then(() => {
      setReplyText(''); 
      setReplyingTo(null);
      getReviewsByDoctorId(hospitalID);
    }).catch(err => {
      console.error('Failed to submit reply:', err);
      setModalTitle('Submission Failed'); setModalMessage('An error occurred while submitting your reply.'); setModalVariant('error'); setModalShow(true);
    });
  };
  
  const handleEditClick = (item) => {
    setEditingItem({ id: item.id, text: item.text });
    setReplyingTo(null);
  };

  const handleEditCancel = () => {
    setEditingItem({ id: null, text: '' });
  };
  
  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!editingItem.text.trim()) {
      setModalTitle("Error"); 
      setModalMessage("Reply cannot be empty."); 
      setModalVariant("error"); 
      setModalShow(true); 
      return;
    }
    
    const originalFeedback = reviews.find(r => r.id === editingItem.id && r.type === 'feedback');

    if (!originalFeedback) {
        console.error("Could not find the original feedback item to update.");
        setModalTitle("Error"); 
        setModalMessage("An unexpected error occurred. Could not find the item to update."); 
        setModalVariant("error"); 
        setModalShow(true);
        return;
    }
    
    updateReviewFeedback(originalFeedback, editingItem.text)
      .then(() => {
        handleEditCancel();
        getReviewsByDoctorId(loginData.hospitalID);
        setModalTitle('Success'); setModalMessage('The reply has been updated.'); setModalVariant('success'); setModalShow(true);

      })
      .catch(err => {
        console.error("Update failed:", err);
        setModalTitle("Error"); 
        setModalMessage("Could not update the reply."); 
        setModalVariant("error"); 
        setModalShow(true);
      });
  };
  const handleDelete = (item) => {
    deleteReviewFeedback(item)
      .then(() => {
        setModalShow(false);
        getReviewsByDoctorId(hospitalID);
        setModalTitle('Success'); setModalMessage('The reply has been deleted.'); setModalVariant('success'); setModalShow(true);
      })
      .catch(err => {
        setModalShow(false);
        console.error("Delete failed:", err);
        setModalTitle('Error'); setModalMessage('Could not delete the reply.'); setModalVariant('error'); setModalShow(true);
      });
  };


  const getAuthorInfo = (authorId) => {
    const doctorProfile = doctors.find(doc => doc.hospitalID == authorId);
    if (doctorProfile) return { name: `Dr. ${doctorProfile.firstName} ${doctorProfile.lastName}`, avatar: doctorProfile.uploadPhoto ? `${imgUrl}${doctorProfile.uploadPhoto}` : "assets/img/default-doctor.jpg" };
    const patientProfile = profiles.find(p => p.PatientID == authorId);
    if (patientProfile) {
      const name = `${patientProfile.firstName || ''} ${patientProfile.lastName || ''}`.trim();
      return { name: name || "Anonymous Patient", avatar: patientProfile.uploadPhoto ? `${imgUrl}${patientProfile.uploadPhoto}` : "assets/img/patients/patient.jpg" };
    }
    return { name: "Anonymous User", avatar: "assets/img/patients/patient.jpg" };
  };
  
  return (
    <div>
        <div className="page-header filter-bar rounded mb-4">
        <div className="row align-items-center">
          <div className="col">
            <h4 className="page-title">Reviews</h4>
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/doctor/dashboard">Doctor Dashboard</Link>
              </li>
              <li className="breadcrumb-item active">Reviews</li>
            </ul>
          </div>
        </div>
      </div>
    <div className="card">
      {/* --- 2. Add the Helmet component --- */}
      <Helmet>
        <title>My Reviews | Medisevas</title>
        <meta name="description" content="View and manage patient reviews and your feedback." />
      </Helmet>
     

      <style>{`
        .rating .fa-star.filled { color: #f4c150; }
        .comment-actions a { margin-left: 10px; font-size: 0.8em; }
        .comment-actions a:first-child { margin-left: 0; }
      `}</style>
      <div className="card-body">
        <div className="doc-review review-listing">
          <ul className="comments-list">
            {reviewLoading && (!reviews || reviews.length === 0) ? (
              <div className="text-center p-5"><div className="spinner-border text-primary" role="status"><span className="sr-only">Loading...</span></div></div>
            ) : reviews && reviews.filter(r => r.type === 'review').length > 0 ? (
              reviews.filter(r => r.type === 'review').map((review) => {
                const reviewer = getAuthorInfo(review.patientId);
                const replies = reviews.filter(reply => reply.type === 'feedback' && reply.reviewId === review.id);

                return (
                  <li key={review.id}>
                    <div className="comment">
                      <img className="avatar avatar-sm rounded-circle" alt={reviewer.name} src={reviewer.avatar} />
                      <div className="comment-body">
                        <div className="meta-data" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <div>
                            <span className="comment-author" style={{ fontWeight: 'bold', marginRight: '10px' }}>{reviewer.name}</span>
                            <span className="comment-date text-muted small">{formatTimeAgo(review.createdAt)}</span>
                          </div>
                          <div className="review-count rating" style={{ position: 'relative' }}>{renderStars(review.stars)}</div>
                        </div>
                        {review.title && <p className="comment-content font-weight-bold">{review.title}</p>}
                        <p className="comment-content">{review.text}</p>
                        <div className="comment-reply">
                          <a className="comment-btn  text-info" href="#" onClick={(e) => { e.preventDefault(); setReplyingTo(replyingTo === review.id ? null : review.id); setReplyText(''); }}>
                            <i className="fas fa-reply text-info"></i> Reply
                          </a>
                        </div>
                        {replyingTo === review.id && (
                          <div className="card-body mt-3 p-3" style={{ border: '1px solid #eee', borderRadius: '5px' }}>
                            <form onSubmit={(e) => handleReplySubmit(e, review)}>
                              <div className="form-group"><label className='font-weight-bold'>Your Reply to {reviewer.name}</label><textarea className="form-control" rows="3" value={replyText} onChange={(e) => setReplyText(e.target.value)} autoFocus></textarea></div>
                              <div className="submit-section">
                                <button type="submit" className="btn btn-primary btn-sm" disabled={reviewSubmitting}>{reviewSubmitting ? 'Submitting...' : 'Submit Reply'}</button>
                                <button type="button" className="btn btn-light btn-sm ml-2" onClick={() => setReplyingTo(null)}>Cancel</button>
                              </div>
                            </form>
                          </div>
                        )}
                      </div>
                    </div>
                    {replies.length > 0 && (
                      <ul className="comments-list reply-list">
                        {replies.map(reply => {
                          const replyAuthor = getAuthorInfo(reply.PatientID);
                          const isReplyAuthor = loginData?.hospitalID && loginData.hospitalID === reply.PatientID;
                          return (
                            <li key={reply.id}>
                              <div className="comment">
                                <img className="avatar avatar-sm rounded-circle" alt={replyAuthor.name} src={replyAuthor.avatar} />
                                <div className="comment-body">
                                    <div className="meta-data">
                                        <span className="comment-author">{replyAuthor.name}</span>
                                        <span className="comment-date text-muted small ml-2">{formatTimeAgo(reply.createdAt)}</span>
                                    </div>
                                    {editingItem.id === reply.id ? (
                                        <form onSubmit={handleEditSubmit}>
                                            <textarea className="form-control form-control-sm" value={editingItem.text} onChange={(e) => setEditingItem(prev => ({...prev, text: e.target.value}))} rows="2" autoFocus />
                                            <div className="submit-section mt-2">
                                                <button type="submit" className="btn btn-primary btn-sm" disabled={reviewSubmitting}>Save</button>
                                                <button type="button" className="btn btn-light btn-sm ml-2" onClick={handleEditCancel}>Cancel</button>
                                            </div>
                                        </form>
                                    ) : (
                                        <p className="comment-content">{reply.text}</p>
                                    )}
                                   
                                </div>
                              </div>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </li>
                );
              })
            ) : (
              <div className="p-4 text-center">
                <h5>You have no reviews yet.</h5>
              </div>
            )}
          </ul>
        </div>
      </div>
      <MessageModal
        show={modalShow}
        onClose={() => { setModalShow(false); setItemToDelete(null); }}
        title={modalTitle}
        message={modalMessage}
        variant={modalVariant}
      />
    </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  reviews: state.reviews.reviews,
  reviewLoading: state.reviews.loading,
  reviewSubmitting: state.reviews.submitting,
  profiles: state.patientProfile?.profiles || [],
  doctors: state.doctorList?.doctors || [],
});

const mapDispatchToProps = {
  getDoctorProfileById,
  getAllDoctors,
  getAllPatientProfiles,
  getReviewsByDoctorId,
  createReviewFeedback,
  updateReviewFeedback,
  deleteReviewFeedback
};

export default connect(mapStateToProps, mapDispatchToProps)(Reviews);